import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { stApi, errorMessage } from './api'
import { runInBrowser } from './browserRunner'
import StPracticeAdmin from './StPracticeAdmin'
import './stDiagnostic.css'

const fmt = (d) => (d ? new Date(d).toLocaleString() : '—')
const SECTION_IDS = ['A', 'B', 'C', 'D', 'E', 'F']

// Runs hidden tests for submitted code in this browser (isolated sandbox) and stores what
// each test printed; the server compares it with the expected output. If the runner itself
// fails (e.g. Python cannot load), nothing is stored for that answer and it stays pending.
async function gradeCodeInBrowser({ attemptId, force } = {}, onProgress = () => {}) {
  const params = new URLSearchParams()
  if (attemptId) params.set('attemptId', attemptId)
  if (force) params.set('force', '1')
  const { data } = await stApi.get(`/admin/code-grading?${params}`)
  const jobs = data.jobs
  const totalRuns = jobs.reduce((n, j) => n + j.inputs.length, 0)
  let doneRuns = 0
  onProgress({ doneRuns, totalRuns, answers: jobs.length })
  // One answer at a time, so results for the same attempt are stored in order.
  for (const job of jobs) {
    const outputs = []
    for (const input of job.inputs) {
      const r = await runInBrowser(job.language, job.code, input)
      if (r.loadFailed) throw new Error(`Could not run ${job.language === 'python' ? 'Python' : 'JavaScript'} in this browser: ${r.error}`)
      // A timeout or error counts as that test failing; the message is kept as its output.
      outputs.push(r.output || r.error || '')
      doneRuns += 1
      onProgress({ doneRuns, totalRuns, answers: jobs.length, current: `${job.name} · ${job.questionId}` })
    }
    await stApi.post(`/admin/attempts/${job.attemptId}/code-results`, { questionId: job.questionId, hash: job.hash, outputs })
  }
  return jobs.length
}

export default function StAdmin() {
  const [state, setState] = useState('loading')
  const [error, setError] = useState('')
  const [attempts, setAttempts] = useState([])
  const [detailId, setDetailId] = useState(null)
  const [detailVersion, setDetailVersion] = useState(0)
  const [busy, setBusy] = useState('')
  const [grading, setGrading] = useState({ status: 'idle' })
  const [tab, setTab] = useState(() => { try { return localStorage.getItem('stAdminTab') || 'diagnostic' } catch { return 'diagnostic' } })
  const chooseTab = (t) => {
    setTab(t)
    try { localStorage.setItem('stAdminTab', t) } catch { /* not important */ }
  }
  const gradingRef = useRef(false)

  const load = useCallback(async () => {
    const { data } = await stApi.get('/admin/attempts')
    setAttempts(data.attempts)
  }, [])

  // Grades any submitted code that has not been graded yet. Safe to call repeatedly.
  const gradeCode = useCallback(async (options = {}) => {
    if (gradingRef.current) return
    gradingRef.current = true
    setGrading({ status: 'running', doneRuns: 0, totalRuns: 0 })
    try {
      const graded = await gradeCodeInBrowser(options, p => setGrading({ status: 'running', ...p }))
      setGrading({ status: graded ? 'done' : 'idle', answers: graded })
      if (graded) {
        await load()
        setDetailVersion(v => v + 1)
      }
    } catch (err) {
      setGrading({ status: 'error', message: errorMessage(err) })
    } finally {
      gradingRef.current = false
    }
  }, [load])

  useEffect(() => {
    stApi.get('/me')
      .then(({ data }) => {
        if (!data.isAdmin) return setState('forbidden')
        return load().then(() => setState('ready'))
      })
      .catch(err => setState(err?.response?.status === 401 ? 'login' : (setError(errorMessage(err)), 'error')))
  }, [load])

  // Grade on open, then keep picking up new submissions while this page stays open.
  useEffect(() => {
    if (state !== 'ready') return
    gradeCode()
    const timer = setInterval(() => load().then(() => gradeCode()).catch(() => {}), 60000)
    return () => clearInterval(timer)
  }, [state, gradeCode, load])

  const act = async (key, fn) => {
    setBusy(key)
    setError('')
    try {
      await fn()
      await load()
      gradeCode()
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy('')
    }
  }

  if (state === 'loading') return <div className="std"><div className="std-wrap">Loading…</div></div>
  if (state === 'login') return <div className="std"><div className="std-wrap"><div className="std-card"><h2>Please sign in</h2><Link className="std-btn" to="/login">Sign in</Link></div></div></div>
  if (state === 'forbidden') return <div className="std"><div className="std-wrap"><div className="std-alert std-alert-bad">This page is for admins only.</div></div></div>
  if (state === 'error') return <div className="std"><div className="std-wrap"><div className="std-alert std-alert-bad">{error}</div></div></div>

  const inProgress = attempts.filter(a => !a.archived && a.status === 'in_progress' && !a.expired).length

  return (
    <div className="std">
      <div className="std-wrap-wide">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1>Software Testing — admin</h1>
            <p className="std-muted" style={{ margin: 0 }}>Watch candidates live, follow diagnostic attempts and practice progress.</p>
          </div>
          <Link className="std-btn" to="/admin/software-testing/live">
            ● Watch live{inProgress > 0 && ` (${inProgress} in progress)`}
          </Link>
        </div>
        {error && <div className="std-alert std-alert-bad">{error}</div>}
        {grading.status === 'running' && (
          <div className="std-alert std-alert-info">
            Grading submitted code in this browser… {grading.totalRuns ? `${grading.doneRuns}/${grading.totalRuns} tests` : 'loading'}
            {grading.current && ` · ${grading.current}`}. Keep this page open until it finishes.
          </div>
        )}
        {grading.status === 'done' && <div className="std-alert std-alert-ok">Graded {grading.answers} code answer(s); scores are saved.</div>}
        {grading.status === 'error' && (
          <div className="std-alert std-alert-bad">
            Code grading stopped: {grading.message} Nothing was saved for the remaining answers.{' '}
            <button className="std-btn std-btn-sm" onClick={() => gradeCode()}>Try again</button>
          </div>
        )}

        <div className="std-tabs" style={{ margin: '16px 0 0' }}>
          <button className={`std-tab ${tab === 'diagnostic' ? 'std-tab-active' : ''}`} onClick={() => chooseTab('diagnostic')}>Diagnostic</button>
          <button className={`std-tab ${tab === 'practice' ? 'std-tab-active' : ''}`} onClick={() => chooseTab('practice')}>Practice progress</button>
        </div>

        {tab === 'practice' ? <div style={{ marginTop: 16 }}><StPracticeAdmin /></div> : (<>
        <div className="std-card" style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2>Attempts ({attempts.length})</h2>
            <button className="std-btn std-btn-ghost std-btn-sm" onClick={() => act('refresh', async () => {})}>Refresh</button>
          </div>
          <div className="std-table-wrap">
            <table className="std-table">
              <thead><tr><th>Candidate</th><th>Lang</th><th>Status</th><th>Started</th>{SECTION_IDS.map(s => <th key={s}>{s}</th>)}<th></th></tr></thead>
              <tbody>
                {attempts.map(a => (
                  <tr key={a._id}>
                    <td><strong>{a.name}</strong><div className="std-muted std-small">{a.email}</div></td>
                    <td>{a.language}</td>
                    <td>
                      <span className={`std-badge ${a.status === 'graded' ? 'std-badge-ok' : a.expired ? 'std-badge-bad' : ''}`}>{a.expired ? 'expired' : a.status.replace('_', ' ')}</span>
                      {a.archived && <div><span className="std-badge" title={`by ${a.archivedBy}`}>archived {new Date(a.archivedAt).toLocaleDateString()}</span></div>}
                      {a.grading?.pendingReview && <div><span className="std-badge std-badge-warn">needs review</span></div>}
                      {a.autoSubmitted && <div className="std-muted std-small">auto-submitted</div>}
                    </td>
                    <td className="std-small">{fmt(a.startedAt)}</td>
                    {SECTION_IDS.map(s => {
                      const sec = a.grading?.sections?.[s]
                      return <td key={s}>{sec ? <span className={`std-badge std-level-${sec.level}`} title={sec.level}>{sec.percent}%</span> : '—'}</td>
                    })}
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className="std-btn std-btn-ghost std-btn-sm" onClick={() => setDetailId(a._id)}>View</button>{' '}
                      {(a.status !== 'in_progress' || a.expired) && (
                        <button className="std-btn std-btn-ghost std-btn-sm" disabled={!!busy} onClick={() => act(`regrade-${a._id}`, () => stApi.post(`/admin/attempts/${a._id}/regrade`))}>
                          {busy === `regrade-${a._id}` ? 'Grading…' : 'Regrade'}
                        </button>
                      )}{' '}
                      {!a.archived && (
                        <button className="std-btn std-btn-danger std-btn-sm" disabled={!!busy}
                          onClick={() => window.confirm(`Archive ${a.name}'s attempt so they can retake the diagnostic? The archived attempt stays here for review.`) && act(`archive-${a._id}`, () => stApi.post(`/admin/attempts/${a._id}/archive`))}>
                          Archive &amp; allow retake
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {attempts.length === 0 && <tr><td colSpan={11} className="std-muted">No attempts yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        {detailId && (
          <AttemptDetail key={`${detailId}-${detailVersion}`} id={detailId} onClose={() => setDetailId(null)} onChanged={load}
            gradingBusy={grading.status === 'running'}
            onRegradeCode={() => gradeCode({ attemptId: detailId, force: true })} />
        )}
        </>)}
      </div>
    </div>
  )
}

function OverrideForm({ attemptId, overrideKey, max, current, onSaved }) {
  const [score, setScore] = useState(current?.score ?? '')
  const [note, setNote] = useState(current?.note ?? '')
  const [msg, setMsg] = useState('')
  const save = async () => {
    setMsg('')
    try {
      await stApi.post(`/admin/attempts/${attemptId}/override`, { key: overrideKey, score: Number(score), note })
      setMsg('Saved')
      onSaved()
    } catch (err) {
      setMsg(errorMessage(err))
    }
  }
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 8 }}>
      <span className="std-small"><strong>Reviewer score</strong> (0–{max}):</span>
      <input type="number" min="0" max={max} value={score} onChange={e => setScore(e.target.value)} style={{ width: 80 }} />
      <input type="text" value={note} placeholder="Note shown to the candidate (optional)" onChange={e => setNote(e.target.value)} style={{ flex: '1 1 240px', width: 'auto' }} />
      <button className="std-btn std-btn-sm" disabled={score === ''} onClick={save}>Save override</button>
      {msg && <span className="std-small std-muted">{msg}</span>}
    </div>
  )
}

function TestResults({ tests }) {
  if (!tests) return null
  if (tests.notAttempted) return <div className="std-muted std-small">Not attempted (starter code unchanged) — 0/{tests.total}</div>
  return (
    <>
      <div className="std-small">
        {tests.pending
          ? <span className="std-badge std-badge-warn">Hidden tests not run yet — they run automatically in this browser</span>
          : <><strong>Hidden tests: {tests.passed}/{tests.total}</strong>{tests.gradedAt && <span className="std-muted"> · graded {fmt(tests.gradedAt)}</span>}</>}
      </div>
      {tests.results?.length > 0 && (
        <table className="std-table" style={{ marginTop: 6 }}>
          <thead><tr><th>Input</th><th>Expected</th><th>Got</th><th></th></tr></thead>
          <tbody>
            {tests.results.map((r, i) => (
              <tr key={i}><td><code>{r.input}</code></td><td><code>{r.expected}</code></td><td><code>{r.got}</code></td><td>{r.pass ? '✓' : '✗'}</td></tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  )
}

function RunHistory({ runs }) {
  if (!runs.length) return <div className="std-small std-muted">No sample runs.</div>
  return (
    <details>
      <summary className="std-small">Sample runs ({runs.length})</summary>
      {runs.map((r, i) => (
        <div key={r._id} className="std-card" style={{ padding: 10, margin: '8px 0' }}>
          <div className="std-small std-muted">
            Run {i + 1} · {fmt(r.at)} · {r.customInput ? 'own input' : 'sample input'}
            {r.matches === true && ' · matched sample'}{r.matches === false && ' · did not match sample'}{r.error && ` · failed: ${r.error}`}
          </div>
          <div className="std-grid-2">
            <div><div className="std-small std-muted">Code</div><pre className="std-pre" style={{ maxHeight: 240 }}>{r.code}</pre></div>
            <div>
              <div className="std-small std-muted">Input</div><pre className="std-pre std-pre-light">{r.stdin}</pre>
              <div className="std-small std-muted">Output</div><pre className="std-pre std-pre-light" style={{ maxHeight: 160 }}>{r.output || '(none)'}</pre>
            </div>
          </div>
        </div>
      ))}
    </details>
  )
}

function AttemptDetail({ id, onClose, onChanged, onRegradeCode, gradingBusy }) {
  const [data, setData] = useState(null)
  const [error, setError] = useState('')

  const load = useCallback(() => stApi.get(`/admin/attempts/${id}`).then(({ data }) => setData(data)).catch(err => setError(errorMessage(err))), [id])
  useEffect(() => { load() }, [load])

  const refresh = () => { load(); onChanged() }

  if (error) return <div className="std-card"><div className="std-alert std-alert-bad">{error}</div></div>
  if (!data) return <div className="std-card">Loading attempt…</div>

  const { attempt, runs = [], sections, answerKey } = data
  const items = attempt.grading?.items || {}
  const overrides = attempt.overrides || {}

  return (
    <div className="std-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
        <div>
          <h2 style={{ marginBottom: 2 }}>{attempt.name} <span className="std-muted std-small">{attempt.email}</span></h2>
          <div className="std-muted std-small">
            {attempt.language} · started {fmt(attempt.startedAt)} · submitted {fmt(attempt.submittedAt)}{attempt.autoSubmitted && ' (auto)'} · status {attempt.status}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {attempt.status !== 'in_progress' && (
            <button className="std-btn std-btn-ghost std-btn-sm" disabled={gradingBusy} onClick={onRegradeCode}
              title="Run this attempt's code against the hidden tests again, in this browser">
              {gradingBusy ? 'Grading…' : 'Re-run hidden tests'}
            </button>
          )}
          <button className="std-btn std-btn-ghost std-btn-sm" onClick={onClose}>Close</button>
        </div>
      </div>

      {attempt.grading?.sections && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '12px 0' }}>
          {Object.entries(attempt.grading.sections).map(([sid, s]) => (
            <span key={sid} className={`std-badge std-level-${s.level}`}>{sid}. {s.title}: {s.percent}% · {s.level}</span>
          ))}
        </div>
      )}

      {sections.map(section => (
        <div key={section.id} style={{ marginTop: 18 }}>
          <h3 style={{ borderBottom: '1px solid var(--std-border)', paddingBottom: 6 }}>{section.id}. {section.title}</h3>
          {section.questions.map(q => {
            const answer = attempt.answers?.[q.id]
            const item = items[q.id]
            if (q.type === 'mcq') {
              return (
                <div key={q.id} className="std-small" style={{ margin: '6px 0' }}>
                  <strong>{q.id}</strong> {answer === undefined ? <span className="std-muted">no answer</span> : q.options[answer]}{' '}
                  {answer !== undefined && (answer === answerKey[q.id] ? '✓' : <span className="std-muted">✗ (correct: {q.options[answerKey[q.id]]})</span>)}
                </div>
              )
            }
            if (q.type === 'debug' || q.type === 'code') {
              const hyp = attempt.hypotheses?.[q.id]
              return (
                <div key={q.id} style={{ margin: '12px 0' }}>
                  <strong>{q.id}. {q.title}</strong>
                  {q.type === 'debug' && (
                    <div className="std-card" style={{ margin: '8px 0', padding: 12 }}>
                      <div className="std-small std-muted">Hypothesis {hyp ? `(locked ${fmt(hyp.at)}; first code edit ${fmt(attempt.firstEditAt?.[q.id])})` : ''}</div>
                      <p style={{ whiteSpace: 'pre-wrap', margin: '4px 0' }}>{hyp?.text || <span className="std-muted">No hypothesis written</span>}</p>
                      {item?.hypothesis && (
                        <div className="std-small">
                          AI score: <strong>{item.hypothesis.pending ? 'pending' : `${item.hypothesis.score}/3`}</strong> — {item.hypothesis.feedback || item.hypothesis.error}
                          {overrides[`${q.id}.hypothesis`] && <span> · <strong>override: {overrides[`${q.id}.hypothesis`].score}/3</strong></span>}
                        </div>
                      )}
                      {item && <OverrideForm attemptId={attempt._id} overrideKey={`${q.id}.hypothesis`} max={3} current={overrides[`${q.id}.hypothesis`]} onSaved={refresh} />}
                    </div>
                  )}
                  <details><summary className="std-small">Submitted code</summary><pre className="std-pre">{answer || q.starter}</pre></details>
                  <RunHistory runs={runs.filter(r => r.questionId === q.id)} />
                  <TestResults tests={item?.tests} />
                </div>
              )
            }
            if (q.type === 'tests') {
              return (
                <div key={q.id} style={{ margin: '12px 0' }}>
                  <strong>{q.id}. {q.title}</strong>
                  {item ? (
                    <>
                      <div className="std-small">Caught <strong>{item.killed}/{item.mutantCount}</strong> mutants · {item.validRows}/{item.totalRows} tests match the spec</div>
                      <table className="std-table" style={{ marginTop: 6 }}>
                        <thead><tr><th>age</th><th>day</th><th>expected</th><th>spec says</th><th>why</th></tr></thead>
                        <tbody>{item.rows.map((r, i) => (
                          <tr key={i} style={{ color: r.valid ? undefined : 'var(--std-bad)' }}><td>{r.age}</td><td>{r.day}</td><td>{r.expected}</td><td>{String(r.specSays)}</td><td>{r.why}</td></tr>
                        ))}</tbody>
                      </table>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                        {item.mutants.map(m => <span key={m.id} className={`std-badge ${m.killed ? 'std-badge-ok' : 'std-badge-bad'}`} title={m.killedBy || 'not caught'}>{m.killed ? '✓' : '✗'} {m.description}</span>)}
                      </div>
                    </>
                  ) : <pre className="std-pre std-pre-light">{JSON.stringify(answer || [], null, 2)}</pre>}
                </div>
              )
            }
            if (q.type === 'review') {
              return (
                <div key={q.id} style={{ margin: '12px 0' }}>
                  <strong>{q.id}. {q.title}</strong>
                  <div className="std-small std-muted" style={{ marginTop: 6 }}>Problems identified</div>
                  <p style={{ whiteSpace: 'pre-wrap', margin: '4px 0' }}>{answer?.problems || <span className="std-muted">—</span>}</p>
                  <div className="std-small std-muted">Restructuring plan</div>
                  <pre className="std-pre std-pre-light" style={{ whiteSpace: 'pre-wrap' }}>{answer?.refactor || '—'}</pre>
                  {item && (
                    <div className="std-small">
                      AI score: <strong>{item.pending ? 'pending' : `${item.score}/10`}</strong>
                      {!item.pending && ` (smells ${item.smellScore}/6, plan ${item.refactorScore}/4)`} — {item.feedback || item.error}
                      {overrides[q.id] && <span> · <strong>override: {overrides[q.id].score}/10</strong></span>}
                    </div>
                  )}
                  {item && <OverrideForm attemptId={attempt._id} overrideKey={q.id} max={10} current={overrides[q.id]} onSaved={refresh} />}
                </div>
              )
            }
            return null
          })}
        </div>
      ))}
    </div>
  )
}
